import React from "react";
import "./MessageDisplay.css";
import ToolTimeline from "../ToolTimeline";
import MessageProfile from "./components/MessageProfile";
import MarkdownContent from "./components/MarkdownContent";
import MessageFeedback from "./components/MessageFeedback";
import ThinkingIndicator, {
  resolveThinkingLabel,
} from "./components/ThinkingIndicator";

/**
 * Top-level renderer for a single chat message. Delegates the actual
 * rendering work to focused sub-components:
 *
 *   - MessageProfile    : avatar / role indicator
 *   - MarkdownContent   : ReactMarkdown wrapper with our code/table overrides
 *   - ToolTimeline      : tool-call timeline for assistant messages
 *   - MessageFeedback   : Yes/No accuracy prompt + comment form
 *   - ThinkingIndicator : "Thinking…" status pill while a run is in flight
 */
const MessageDisplay = ({
  message,
  index,
  isStreaming,
  clientId,
  conversationId,
}) => {
  const isAssistant = message.role === "assistant";
  // Feedback needs a server-issued message id to address the row; it's absent
  // on the greeting and on the in-flight placeholder until the run finalises.
  const showFeedback =
    !message.thinking && index !== 0 && isAssistant && message.id != null;

  // While the run is in flight the assistant turn has no content to render, so
  // the message body is replaced by the status pill. `thinking` is cleared by
  // useAgentRun on the first token / `final` / `error`, at which point the
  // normal body (and the tool timeline) takes over.
  const isThinking = isAssistant && Boolean(message.thinking);

  return (
    <div className={`message--wrapper ${message.role}`}>
      <div className={`message-content ${message.role}-body--container`}>
        <div className="message-profile--wrapper">
          <MessageProfile role={message.role} />
        </div>
        <div className="message-body--container">
          {isThinking ? (
            <ThinkingIndicator label={resolveThinkingLabel(message)} />
          ) : (
            <>
              <div className={`message--body ${message.role}--prompt-body `}>
                <div className="markdown-content">
                  <MarkdownContent message={message} />
                </div>
                {/* Tool-call timeline (no-op for legacy messages without `steps`). */}
                {isAssistant && <ToolTimeline steps={message.steps} />}
              </div>
              {showFeedback && (
                <MessageFeedback
                  message={message}
                  clientId={clientId}
                  conversationId={conversationId}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MessageDisplay;
